import { User } from './user';

export enum UsingMode {
    God = 'God',
    Anonymous = 'Anonymous',
    User = 'User'
}

export enum ModerationStatus {
    Pending = 'pending',
    Approved = 'approved',
    Declined = 'declined'
}

export abstract class AbstractReview {
    id: string;
    user: string;
    created: Date;
    isPublished: boolean;
    rating: number;
    comments: IReviewComment[];
}

export class Review extends AbstractReview {
    id: string;
    user: string;
    created: Date;
    isPublished: boolean;
    rating: number;
    comments: IReviewComment[];
}

export interface IReviewComment {
    id: string;
    author: Author;
    created: Date;
    updated: Date;
    message: string;
    moderationStatus: ModerationStatus;
    isModeratorComment?: boolean;
    moderationComment: string;
    moderationDate: Date;
    moderator: Author;
}

class Author {
    id: string;
    name: string;
}

export class UserComment {
    parentReview: AbstractEnhancedReview;
    id: string;
    userId: string;
    userName: string;
    created: Date;
    message: string;
    truncatedMessage: string;
    isTruncated: boolean;
    status: ModerationStatus;
    isModeratorComment?: boolean;
    isExpanded: boolean;
}

export class AdminComment {
    parentReview: AbstractEnhancedReview;
    moderatorId: string;
    moderatorName: string;
    created: Date;
    updated: Date;
    message: string;
    truncatedMessage: string;
    isTruncated: boolean;
    isExpanded: boolean;
}

export class CommentPack {
    comment: UserComment;
    answer: AdminComment;

    constructor(review?: AbstractEnhancedReview) {
        this.comment = new UserComment();
        this.answer = new AdminComment();

        if (review) {
            this.comment.parentReview = review;
            this.answer.parentReview = review;
        }
    }
}

export abstract class AbstractEnhancedReview {
    protected decoratedReview: Review;

    abstract get id(): string;
    abstract get userId(): string;
    abstract get created(): Date;
    abstract get isPublished(): boolean;
    abstract get rating(): number;

    abstract get base(): CommentPack;
    abstract get comments(): CommentPack[];

    abstract updateComment(userComment: UserComment, raw: IReviewComment): UserComment;
    abstract addComment(raw: IReviewComment): CommentPack;
}

export class EnhancedReview extends AbstractEnhancedReview {
    private _base: CommentPack;
    private _comments: CommentPack[];

    get id(): string {
        return this.decoratedReview.id;
    }
    get userId(): string {
        return this.decoratedReview.user;
    }
    get created(): Date {
        return this.decoratedReview.created;
    }
    get isPublished(): boolean {
        return this.decoratedReview.isPublished;
    }
    get rating(): number {
        return this.decoratedReview.rating;
    };

    get base(): CommentPack {
        return this._base;
    }
    get comments(): CommentPack[] {
        return this._comments;
    }

    constructor(raw: AbstractReview) {
        super();
        this.decoratedReview = raw;
        this._comments = [];

        if (!this.decoratedReview.comments.length) {
            return;
        }

        const packs = this.decoratedReview.comments.map(c => {
            const pack = new CommentPack(this);
            pack.comment.message = c.message;
            pack.comment.id = c.id;
            pack.comment.status = c.moderationStatus;
            pack.comment.truncatedMessage = this.truncateMessage(c.message);
            pack.comment.isTruncated = pack.comment.truncatedMessage
                && pack.comment.truncatedMessage.length !== pack.comment.message.length;
            if (c.isModeratorComment !== undefined) {
                pack.comment.isModeratorComment = c.isModeratorComment;
            }
            pack.comment.userId = c.author.id;
            pack.comment.userName = c.author.name;
            pack.comment.created = c.created;

            if (c.moderationStatus !== ModerationStatus.Pending && c.isModeratorComment !== true) {
                pack.answer.message = c.moderationComment;
                pack.answer.truncatedMessage = this.truncateMessage(c.moderationComment);
                pack.answer.isTruncated = pack.answer.truncatedMessage
                    && pack.answer.truncatedMessage.length !== pack.answer.message.length;
                pack.answer.created = c.moderationDate;
                pack.answer.moderatorId = c.moderator.id;
                pack.answer.moderatorName = c.moderator.name;
                pack.answer.updated = c.updated;
            }

            return pack;
        });


        let i = -1;
        // get base review from comments
        packs.some((p, index) => {
            // if there is no defined property isModeratorComment, it means this is moderator base review 
            if (p.comment.isModeratorComment !== undefined) {
                return false;
            }

            this._base = new CommentPack(this);
            this._base.comment = p.comment;

            if (p.comment.status !== ModerationStatus.Pending) {
                this._base.answer = p.answer;
            }

            i = index;

            return true;
        });

        if (i >= 0) {
            packs.splice(i, 1);
        }

        this._comments = packs;
    }

    addComment(raw: IReviewComment): CommentPack {
        const pack = new CommentPack(this);
        pack.comment.message = raw.message;
        pack.comment.id = raw.id;
        pack.comment.status = raw.moderationStatus;
        pack.comment.truncatedMessage = this.truncateMessage(raw.message);
        pack.comment.isTruncated = pack.comment.truncatedMessage && pack.comment.truncatedMessage.length !== pack.comment.message.length;
        if (raw.isModeratorComment !== undefined) {
            pack.comment.isModeratorComment = raw.isModeratorComment;
        }
        pack.comment.userId = raw.author.id;
        pack.comment.userName = raw.author.name;
        pack.comment.created = raw.created;

        this._comments.push(pack);
        return pack;
    }
    updateComment(userComment: UserComment, raw: IReviewComment): UserComment {
        if (this._base.comment.id === userComment.id) {
            return this.updateFrom(raw);
        }

        let updatedComment: UserComment;
        this.comments.some(c => {
            if (c.comment.id === userComment.id) {
                c.comment.status = raw.moderationStatus;
                c.comment.message = raw.message;
                if (raw.isModeratorComment !== undefined) {
                    c.comment.isModeratorComment = raw.isModeratorComment;
                }
                c.answer.message = raw.moderationComment;
                c.answer.created = raw.moderationDate;
                c.answer.moderatorId = raw.moderator.id;
                c.answer.moderatorName = raw.moderator.name;
                c.answer.updated = raw.updated;

                updatedComment = c.comment;
                return true;
            }
            return false;
        });

        return updatedComment;
    }

    updateFrom(raw: IReviewComment): UserComment {
        this._base.comment.status = raw.moderationStatus;
        this._base.comment.message = raw.message;
        this._base.comment.truncatedMessage = this.truncateMessage(raw.message);
        this._base.comment.isTruncated = this._base.comment.truncatedMessage
            && this._base.comment.truncatedMessage.length !== this._base.comment.message.length;

        if (raw.moderationStatus === ModerationStatus.Pending) {
            return this._base.comment;
        }

        this._base.answer.message = raw.moderationComment;
        this._base.answer.truncatedMessage = this.truncateMessage(raw.moderationComment);
        this._base.answer.isTruncated = this._base.answer.truncatedMessage
            && this._base.answer.truncatedMessage.length !== this._base.answer.message.length;
        this._base.answer.created = raw.moderationDate;
        this._base.answer.moderatorId = raw.moderator.id;
        this._base.answer.moderatorName = raw.moderator.name;
        this._base.answer.updated = raw.updated;

        return this._base.comment;
    }

    private truncateMessage(message: string) {
        const maxLength = 400;
        if (message && message.length > maxLength) {
            return message.substr(0, maxLength - 3) + '...';
        }

        return message;
    }
}



// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/core/review.ts